"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EXIT_MS = void 0;
exports.motionAllowed = motionAllowed;
exports.usePresence = usePresence;
const react_1 = require("react");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Motion: whether to play it, and how long a closing surface stays.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The curves and durations live in `base.css` (`--ease-out-strong`, `--ease-drawer`, `--dur-*`)
 * beside the keyframes that use them; `design-system/readme.md` § Animation records why this
 * product now moves more than the design system's budget allows. These are the two pieces CSS
 * cannot do on its own: keep a surface mounted while it leaves, and know when not to bother.
 */
/** Exit lengths, in ms — kept equal to the `--dur-*-out` tokens in `base.css`. */
exports.EXIT_MS = {
    dialog: 150,
    sheet: 200,
    toast: 200,
};
/**
 * ⚠️⚠️ **NO ANIMATION ENGINE, NO EXIT — AND THAT IS WHAT KEEPS EVERY TEST HONEST.** jsdom
 * implements neither `Element.animate` nor CSS animations, so a surface held open for its exit
 * there would simply linger, invisible to nothing, and every *"closes on Escape"* assertion in the
 * suite would start racing a timer. A browser with the Web Animations API is a browser that plays
 * keyframes; one without it gets the instant close it always had.
 *
 * ⚠️ **Reduced motion closes instantly as well.** `base.css` already flattens every animation to
 * 0.01ms under the preference, so holding the surface for 200ms would be 200ms of an invisible,
 * inert layer and nothing else.
 */
function motionAllowed() {
    if (typeof window === 'undefined' || typeof Element === 'undefined')
        return false;
    if (typeof Element.prototype.animate !== 'function')
        return false;
    if (typeof window.matchMedia !== 'function')
        return true;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
/**
 * Keeps a surface mounted for `exitMs` after `open` goes false, so it can animate out.
 *
 * ⚠️ **Reopening mid-exit just cancels the exit** — the same element stays mounted and switches
 * back to its open state, rather than unmounting and mounting a second copy.
 */
function usePresence(open, exitMs) {
    const [closing, setClosing] = (0, react_1.useState)(false);
    const [wasOpen, setWasOpen] = (0, react_1.useState)(open);
    /* Adjusted during render, React's documented pattern for state derived from a prop change. */
    if (open !== wasOpen) {
        setWasOpen(open);
        setClosing(!open && exitMs > 0 && motionAllowed());
    }
    (0, react_1.useEffect)(() => {
        if (!closing)
            return;
        const timer = window.setTimeout(() => {
            setClosing(false);
        }, exitMs);
        return () => {
            window.clearTimeout(timer);
        };
    }, [closing, exitMs]);
    return { mounted: open || closing, closing: closing && !open };
}
//# sourceMappingURL=motion.js.map